import React from 'react';
import { observer } from 'mobx-react';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import { isPresent } from '@seedlang/utils';
import { AppUI, ExerciseUI } from '@seedlang/state';
import { Skeleton } from '@mantine/core';
import PodcastButton from 'components/worksheet/podcast_button';
import NoAccessButton from 'components/worksheet/no_access_button';
import PodcastAudioPlayer from './podcast_audio_player';
import DesktopOnly from 'utils/desktop_only';

const Wrapper = styled.div`
  flex: 2;
  position: relative;
  height: fit-content;
  font-size: 16px;
  width: ${props => props.width};
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
  border-radius: ${props => props.borderRadius};
  @media (max-width: 999px) {
    order: -1;
    width: 100%;
  }
  iframe, .youtube-video {
    border-radius: 10px;
    overflow: hidden;
  }
  .icon-wrapper {
    display: inline-block;
    margin: 4px 4px 0 0;
  }
  .youtube-icon {
    display: inline-block;
    svg {
      width: 24px;
      fill: #c4302b;
    }
  }
`;

const CoverWrapper = styled.div`
  flex: 1;
  max-width: ${props => props.maxWidth};
  position: relative;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  audio {
    width: 100%;
  }
  margin: 10px;
`;

const ImageWrapper = styled.a`
  img {
    max-height: ${props => props.maxHeight};
    border-radius: 10px;
  }
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 114px) 1fr;
  gap: 20px;
  width: 100%;
  align-items: stretch;

  @media (max-width: 699px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewImageWrapper = styled.div`
  width: 100%;

  img {
    border-radius: 10px;
    display: block;
    width: 100%;
  }
`;

const PreviewControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  justify-content: center;
  min-width: 0;
`;

@observer
class PodcastPlayer extends React.Component {

  get noAccessMessage() {
    switch (this.props.noAccessReason) {
      case 'create_free_account':
        return 'Register to Try Interactive Player';
      case 'upgrade':
        return 'Upgrade to Unlock Interactive Player';
      case 'become_a_member':
        return 'Upgrade to Unlock Interactive Player';
      default:
        return null;
    }
  }

  renderPreviewLayout() {
    const imageUrl = this.props.imageUrl || this.props.podcastEpisode?.imageUrl || this.props.podcast?.imageUrl;

    return (
      <Wrapper
        width={this.props.width}
        borderRadius={this.props.borderRadius}
      >
        <PreviewGrid>
          <PreviewImageWrapper>
            {
              imageUrl &&
                <img
                  src={imageUrl}
                  alt={this.props.podcast?.title ? `Podcast cover for ${this.props.podcast.title}` : ''}
                />
            }
          </PreviewImageWrapper>
          <Skeleton visible={this.props.loading}>
            <PreviewControls>
              {
                this.props.hasAccess && isPresent(this.props.podcastEpisode) &&
                  <>
                    <PodcastAudioPlayer podcastEpisode={this.props.podcastEpisode} />
                    <PodcastButton
                      hasAccess={this.props.hasAccess}
                      margin='0'
                      height='50px'
                      podcastEpisode={this.props.podcastEpisode}
                      showImage={false}
                    />
                  </>
              }
              {
                !this.props.hasAccess &&
                  <NoAccessButton
                    margin='0'
                    reason={this.props.noAccessReason}
                    message={this.noAccessMessage}
                  />
              }
            </PreviewControls>
          </Skeleton>
        </PreviewGrid>
      </Wrapper>
    );
  }

  render() {
    if (this.props.previewLayout) {
      return this.renderPreviewLayout();
    }

    return (
      <Wrapper
        width={this.props.width}
        borderRadius={this.props.borderRadius}
      >
        <DesktopOnly>
          <CoverWrapper
            maxWidth={AppUI.layout.isMobile ? '170px' : '320px'}
          >
            {
              isPresent(this.props.podcast?.imageUrl) &&
                <ImageWrapper
                  maxHeight={AppUI.layout.isMobile ? '150px' : '300px'}
                >
                  <img
                    src={this.props.podcast?.imageUrl}
                    alt={`Podcast cover for ${this.props.podcast.title}`}
                  />
                </ImageWrapper>
            }
          </CoverWrapper>
        </DesktopOnly>
        {
          this.props.hasAccess && isPresent(this.props.podcastEpisode) &&
            <>
              <PodcastAudioPlayer podcastEpisode={this.props.podcastEpisode} />
              <PodcastButton
                hasAccess={this.props.hasAccess}
                margin='10px 0 5px 0'
                height='50px'
                podcastEpisode={this.props.podcastEpisode}
                showImage={AppUI.layout.isMobile || ExerciseUI.worksheet.youtubeUrlIsValid}
              />
            </>
        }
        {
          !this.props.hasAccess &&
            <NoAccessButton
              margin='10px 0'
              reason={this.props.noAccessReason}
              message={this.noAccessMessage}
            />
        }
      </Wrapper>
    );
  }
}

export default ErrorBoundary(PodcastPlayer);
